import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomIcon } from "../UI";
import "./SearchAndFilterBar.scss";
import AddForm from "../modals/add-item";
import UniversalFilterModal, { getFilterConfig } from "../modals/universal-filter";

const SearchAndFilterBar = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit, // New: callback when search is submitted (click icon or Enter)
  onFilterClick,
  onFilterApply, // New: callback when filters are applied
  onExportClick,
  onAddNewClick, // For loads page - navigate to form page
  onAddClick, // Custom add click handler (for payments, etc.)
  pageType = "loads", // 'loads', 'users', 'customers', 'carriers', 'payments', 'paymentsPayable', 'paymentsReceivable'
  filterConfig = null, // Optional: custom filter config, if null uses getFilterConfig
  activeTab = null, // For statistics page: current active tab (users, loads, payments, etc.)
  searchPlaceholder,
  addButtonLabel,
  addButtonText, // Custom add button text
  filterLabel = "Filter By",
  disableAddModal = false,
  showAddButton = true,
  dateRangeOptions,
  dateRangeValue,
  dateRangeDetail,
  onDateRangeChange,
  onAddSuccess, // Callback when user/customer/carrier is successfully created
  initialFilters = {}, // Initial filter values
  showSearch = true, // For statistics: show search only on users/customers tabs
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [localDateRangeIndex, setLocalDateRangeIndex] = useState(0);
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const dateDropdownRef = useRef(null);

  const showStatisticsActions = pageType === "statistics";

  const getAddButtonText = () => {
    switch (pageType) {
      case "users":
        return "Add New User";
      case "customers":
        return "Add New Customer";
      case "carriers":
        return "Add New Carrier";
      case "payments":
        return "Add New Payment";
      case "loads":
      default:
        return "Add New Load";
    }
  };

  const defaultDateRangeOptions = useMemo(
    () => [
      { label: "Today", value: "today" },
      { label: "This Week", value: "this_week" },
      { label: "This Month", value: "this_month" },
      { label: "This Quarter", value: "this_quarter" },
      { label: "This Year", value: "this_year" },
    ],
    []
  );

  const getSearchPlaceholder = () => {
    switch (pageType) {
      case "users":
        return "Search users...";
      case "customers":
        return "Search customers...";
      case "payments":
        return "Search payments...";
      case "loads":
      default:
        return "Search loads...";
    }
  };

  const resolvedAddButtonText = addButtonText || addButtonLabel || getAddButtonText();
  const resolvedSearchPlaceholder = searchPlaceholder || getSearchPlaceholder();
  const resolvedFilterLabel = filterLabel || "Filter By";

  const resolvedDateOptions = useMemo(() => {
    const sourceOptions =
      (dateRangeOptions && dateRangeOptions.length > 0
        ? dateRangeOptions
        : defaultDateRangeOptions) || [];

    return sourceOptions.map((option) => {
      if (typeof option === "string") {
        return { label: option, value: option };
      }

      return {
        label: option?.label ?? option?.value ?? "",
        value: option?.value ?? option?.label ?? "",
      };
    });
  }, [dateRangeOptions, defaultDateRangeOptions]);

  const currentDateRangeIndex = useMemo(() => {
    if (dateRangeValue) {
      const index = resolvedDateOptions.findIndex(
        (option) => option.value === dateRangeValue
      );
      if (index !== -1) {
        return index;
      }
    }
    return localDateRangeIndex;
  }, [dateRangeValue, resolvedDateOptions, localDateRangeIndex]);

  const currentDateRange =
    resolvedDateOptions[currentDateRangeIndex] || resolvedDateOptions[0];

  useEffect(() => {
    if (!resolvedDateOptions.length) {
      return;
    }

    if (!dateRangeValue && localDateRangeIndex >= resolvedDateOptions.length) {
      setLocalDateRangeIndex(0);
    }
  }, [resolvedDateOptions, dateRangeValue, localDateRangeIndex]);

  useEffect(() => {
    if (!isDateMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target)
      ) {
        setIsDateMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDateMenuOpen]);

  const handlePrimaryAction = () => {
    if (showStatisticsActions) {
      if (onExportClick) {
        onExportClick();
      }
      return;
    }

    // Custom add click handler (for payments, etc.)
    if (onAddClick) {
      onAddClick();
      return;
    }

    // For loads page, navigate to form page
    if (pageType === "loads") {
      if (onAddNewClick) {
        onAddNewClick();
      }
      return;
    }

    // For users, customers, carriers - show modal
    if (!disableAddModal && (pageType === "users" || pageType === "customers" || pageType === "carriers")) {
      setIsOpen(true);
    }
  };

  const handleDateToggle = () => {
    if (!resolvedDateOptions.length) return;
    setIsDateMenuOpen((prev) => !prev);
  };

  const handleDateSelect = (option, index) => {
    if (!option) return;

    if (!dateRangeValue) {
      setLocalDateRangeIndex(index);
    }

    if (onDateRangeChange) {
      onDateRangeChange(option.value, option);
    }

    setIsDateMenuOpen(false);
  };

  const handleFilterClick = () => {
    if (onFilterClick) {
      onFilterClick();
    } else {
      setIsFilterModalOpen(true);
    }
  };

  const handleFilterApply = (filters) => {
    if (onFilterApply) {
      onFilterApply(filters);
    }
    setIsFilterModalOpen(false);
  };

  useEffect(() => {
    setLocalSearchTerm(searchTerm || '');
  }, [searchTerm]);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleSearchSubmit = () => {
    if (onSearchSubmit) {
      onSearchSubmit(localSearchTerm);
    } else if (onSearchChange) {
      onSearchChange(localSearchTerm);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const getFilterConfigForPage = useMemo(() => {
    if (filterConfig) return filterConfig;
    
    const configMap = {
      loads: 'loads',
      users: 'users',
      customers: 'customers',
      carriers: 'carriers',
      paymentsPayable: 'paymentsPayable',
      paymentsReceivable: 'paymentsReceivable',
      statistics: 'statistics'
    };
    
    if (pageType === 'statistics' && activeTab) {
      const statisticConfigMap = {
        users: 'userStatistic',
        loads: 'loadsStatistic',
        payments: 'paymentsStatistic',
        carriers: 'carrierStatistic',
        customers: 'customerStatistic',
        receivable: 'paymentsStatistic',
        payable: 'paymentsStatistic',
        overview: 'statistics'
      };
      const statisticEntityType = statisticConfigMap[activeTab] || 'statistics';
      return getFilterConfig(statisticEntityType);
    }
    
    const entityType = configMap[pageType];
    return entityType ? getFilterConfig(entityType) : null;
  }, [filterConfig, pageType, activeTab]);

  const currentFilterConfig = getFilterConfigForPage;

  const actionsClassName = showStatisticsActions
    ? "actions-section statistics-actions"
    : "actions-section";

  return (
    <>
      <div className="search-filter-bar">
        {showSearch && (
          <div className="search-section">
            <div className="search-input-container">
              <button
                type="button"
                onClick={handleSearchSubmit}
                className="search-icon-button"
                aria-label="Search"
              >
                <CustomIcon name="searcha" size={24} className="search-icon" />
              </button>
              <input
                type="text"
                placeholder={resolvedSearchPlaceholder}
                value={localSearchTerm}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                className="search-input"
              />
            </div>
          </div>
        )}
        <div className={actionsClassName}>
          <button type="button" className="filter-btn" onClick={handleFilterClick}>
            {resolvedFilterLabel}
          </button>

          {showStatisticsActions ? (
            <>
             <button
                type="button"
                className="export-btn"
                onClick={handlePrimaryAction}
              >
                 <CustomIcon 
                    name="share" 
                    size={19} 
                    className="export-btn-icon"
                  />
                {resolvedAddButtonText || "Export"}
              </button>
              <div
                className="date-toggle"
                ref={dateDropdownRef}
              >
                <button
                  type="button"
                  className={`date-toggle-btn${
                    dateRangeDetail ? " date-toggle-btn--with-detail" : ""
                  }`}
                  onClick={handleDateToggle}
                  aria-haspopup="listbox"
                  aria-expanded={isDateMenuOpen}
                >
                  <span className="date-toggle-btn__main">
                    <span className="date-toggle-btn__label">
                      {currentDateRange?.label || "Select Range"}
                    </span>
                    {dateRangeDetail ? (
                      <span className="date-toggle-btn__detail" title={dateRangeDetail}>
                        {dateRangeDetail}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={`date-toggle-btn__caret${
                      isDateMenuOpen ? " open" : ""
                    }`}
                  />
                </button>
                {isDateMenuOpen && resolvedDateOptions.length > 0 && (
                  <div className="date-toggle__menu" role="listbox">
                    {resolvedDateOptions.map((option, index) => {
                      const isActive =
                        (dateRangeValue && option.value === dateRangeValue) ||
                        (!dateRangeValue && index === currentDateRangeIndex);

                      return (
                        <button
                          type="button"
                          key={option.value || option.label || index}
                          className={`date-toggle__menu-item${
                            isActive ? " active" : ""
                          }`}
                          onClick={() => handleDateSelect(option, index)}
                          role="option"
                          aria-selected={isActive}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
             
            </>
          ) : showAddButton ? (
            <button
              type="button"
              className="add-new-btn"
              onClick={handlePrimaryAction}
            >
              <CustomIcon name="plus-01" size={24} type="svg" />
              {resolvedAddButtonText}
            </button>
          ) : null}
        </div>
      </div>
      {showAddButton && !disableAddModal && !showStatisticsActions && (pageType === "users" || pageType === "customers" || pageType === "carriers") && (
        <AddForm 
          isOpen={isOpen} 
          setIsOpen={setIsOpen} 
          formType={pageType}
          onSuccess={onAddSuccess}
        />
      )}
      
      {/* Universal Filter Modal */}
      {currentFilterConfig && !(pageType === 'statistics' && ['loads', 'users', 'receivable', 'payable', 'carriers', 'customers'].includes(activeTab)) && (
        <UniversalFilterModal
          key={`filter-modal-${pageType}-${activeTab || ''}`}
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleFilterApply}
          filterConfig={currentFilterConfig}
          initialFilters={initialFilters}
          title={pageType === 'statistics' && activeTab 
            ? `Filter ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Statistics`
            : `Filter ${pageType.charAt(0).toUpperCase() + pageType.slice(1)}`}
        />
      )}
    </>
  );
};

export default SearchAndFilterBar;
